import type {
	CoursesAudiencePreviewDto,
	CoursesEmailLogsQueryDto,
	CoursesEmailsSearchUsersQueryDto,
	CoursesSendBroadcastDto,
	CoursesUpsertEmailTemplateDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	AdminAudiencePreviewInput,
	AdminAudiencePreviewOutput,
	AdminSendBroadcastInput,
	AdminSendBroadcastOutput,
	AdminListBroadcastsOutput,
	AdminGetBroadcastInput,
	AdminGetBroadcastOutput,
	AdminRetryFailedRecipientsInput,
	AdminRetryFailedRecipientsOutput,
	AdminListEmailTemplatesOutput,
	AdminUpsertEmailTemplateInput,
	AdminUpsertEmailTemplateOutput,
	AdminGetEmailLogsInput,
	AdminGetEmailLogsOutput,
	AdminSearchUsersInput,
	AdminSearchUsersOutput,
	CreateEmailImageUploadUrlInput,
	CreateEmailImageUploadUrlOutput,
} from '../../types/admin-emails.types';

const TAG = 'courses:admin-emails' as const;
const INVALIDATES = [TAG] as const;

export const adminEmailsEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		adminAudiencePreview: builder.mutation<AdminAudiencePreviewOutput, AdminAudiencePreviewInput>({
			query: (body) => ({
				url: 'courses/admin/emails/audience-preview',
				method: 'POST',
				body: body satisfies Wire<CoursesAudiencePreviewDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminSendBroadcast: builder.mutation<AdminSendBroadcastOutput, AdminSendBroadcastInput>({
			query: (body) => ({
				url: 'courses/admin/emails/broadcasts',
				method: 'POST',
				body: body satisfies Wire<CoursesSendBroadcastDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminListBroadcasts: builder.query<AdminListBroadcastsOutput, void>({
			query: () => ({ url: 'courses/admin/emails/broadcasts' }),
			providesTags: [TAG],
		}),
		adminGetBroadcast: builder.query<AdminGetBroadcastOutput, AdminGetBroadcastInput>({
			query: ({ id }) => ({ url: `courses/admin/emails/broadcasts/${encodeURIComponent(id)}` }),
			providesTags: [TAG],
		}),
		adminRetryFailedRecipients: builder.mutation<
			AdminRetryFailedRecipientsOutput,
			AdminRetryFailedRecipientsInput
		>({
			query: ({ broadcast_id }) => ({
				url: `courses/admin/emails/broadcasts/${encodeURIComponent(broadcast_id)}/retry-failed`,
				method: 'POST',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminListEmailTemplates: builder.query<AdminListEmailTemplatesOutput, void>({
			query: () => ({ url: 'courses/admin/emails/templates' }),
			providesTags: [TAG],
		}),
		adminUpsertEmailTemplate: builder.mutation<
			AdminUpsertEmailTemplateOutput,
			AdminUpsertEmailTemplateInput
		>({
			query: (body) => ({
				url: 'courses/admin/emails/templates',
				method: 'PUT',
				body: body satisfies Wire<CoursesUpsertEmailTemplateDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminGetEmailLogs: builder.query<AdminGetEmailLogsOutput, AdminGetEmailLogsInput>({
			query: (params) => ({
				url: 'courses/admin/emails/logs',
				params: params satisfies Wire<CoursesEmailLogsQueryDto>,
			}),
			providesTags: [TAG],
		}),
		adminSearchUsers: builder.query<AdminSearchUsersOutput, AdminSearchUsersInput>({
			query: (params) => ({
				url: 'courses/admin/emails/users-search',
				params: params satisfies Wire<CoursesEmailsSearchUsersQueryDto>,
			}),
			providesTags: [TAG],
		}),
		createEmailImageUploadUrl: builder.mutation<
			CreateEmailImageUploadUrlOutput,
			CreateEmailImageUploadUrlInput
		>({
			query: (body) => ({ url: 'courses/admin/emails/images/upload-url', method: 'POST', body }),
		}),
	}),
});

export const {
	useAdminAudiencePreviewMutation,
	useAdminSendBroadcastMutation,
	useAdminListBroadcastsQuery,
	useLazyAdminListBroadcastsQuery,
	useAdminGetBroadcastQuery,
	useLazyAdminGetBroadcastQuery,
	useAdminRetryFailedRecipientsMutation,
	useAdminListEmailTemplatesQuery,
	useLazyAdminListEmailTemplatesQuery,
	useAdminUpsertEmailTemplateMutation,
	useAdminGetEmailLogsQuery,
	useLazyAdminGetEmailLogsQuery,
	useAdminSearchUsersQuery,
	useLazyAdminSearchUsersQuery,
	useCreateEmailImageUploadUrlMutation,
} = adminEmailsEndpoints;
